import React, { Component } from 'react'
import { contactEmails } from '@adc/constants'
import PropTypes from 'prop-types'
import * as Sentry from '@sentry/browser'
import configment from '@adc/configment'
import { Message, Button, Icon } from '@adc/ui-core'
import Header from '../header'

class ApplicationErrorBoundary extends Component {
  static getDerivedStateFromError(error) {
    return {
      errorCaught: true,
      errorRef: error,
    }
  }

  static propTypes = {
    children: PropTypes.node.isRequired,
    user: PropTypes.object.isRequired,
  }

  state = {
    errorCaught: false,
    errorRef: null,
  }

  componentDidCatch(error, errorInfo) {
    console.error(error.message, errorInfo)

    error.componentStack = errorInfo.componentStack

    if (error instanceof Error) {
      Sentry.configureScope(scope => {
        scope.setTag('user_id', this.props.user.user_id)
        scope.setTag('party_id', this.props.user.party_id)
      })

      // eslint-disable-next-line no-console
      console.info(
        `%cSentry%c Error Captured -- ${error.toString()}`,
        'background: #000; color: #fff; padding: 3px 6px;',
        'background: #c30000; color: #fff; padding: 3px 6px;'
      )
      Sentry.captureException(error)
    }
  }

  render() {
    return this.state.errorCaught ? (
      <ErrorPage error={this.state.errorRef} />
    ) : (
      this.props.children
    )
  }
}

const ErrorPage = ({ error, contactEmail }) => {
  const trace = [error.message, error.componentStack || error.stack].join(' ')

  // NOTE: We put a global here to give us some way to force the client to show us whatever trace it has in any env when
  // we try and repro. If everything is integrated correctly this should also be present in Sentry
  const shouldShowTrace =
    configment.get('ENV', 'prod') === 'dev' || window.__ADC__SHOW_ERROR_TRACES__

  const selectedContactEmail =
    contactEmail ||
    contactEmails?.CUSTOMER_SERVICE_EMAIL ||
    'customerservice@auction.com'

  return (
    <>
      <div className='u-mb-4'>
        <Header.LogoHeader isSticky />
      </div>

      <section
        className='u-ml-4 u-mr-4'
        style={{ position: 'absolute', top: '100px' }}
      >
        <div className='u-mb-4'>
          <Icon
            name='warning sign'
            size='big'
            color='error'
            style={{ float: 'left' }}
          />
          <h2>Something went wrong</h2>
        </div>

        <div className='u-mb-4'>
          Please try reloading the page. Contact{' '}
          <a href={`mailto:${selectedContactEmail}`}>{selectedContactEmail}</a>{' '}
          if this issue persists.
        </div>

        <div className='u-mb-6'>
          <Button role='primary' onClick={() => window.location.reload()}>
            Reload Page
          </Button>
        </div>

        {shouldShowTrace && (
          <Message negative>
            <Message.Header>
              This stack trace is only shown in lower environments
            </Message.Header>
            <hr />
            <pre style={{ maxHeight: 600 }}>{trace}</pre>
          </Message>
        )}
      </section>
    </>
  )
}

ErrorPage.propTypes = {
  error: PropTypes.object,
  contactEmail: PropTypes.string,
}

export default ApplicationErrorBoundary
